import { SubAPI } from "./SubAPI";

export type TaskFilter = {
    // "#293" or "293" also matches the task with that id, which is listed first
    search?: string;
    completed?: boolean;
    blocked?: boolean;
    assignedTo?: number;
    includeArchived?: boolean;

    // narrows the list to one team. Without it the list holds every team the user is on.
    team?: number;

    // "2006-01-02", inclusive
    dueFrom?: string;
    dueTo?: string;

    // the reader's own date, "2006-01-02". An open task whose due date has passed is
    // carried onto it. Without it the server's date stands in.
    today?: string;

    // limits the result to tasks that have no due date
    unscheduled?: boolean;

    // limits the result to one project's tasks and sorts them the way that project
    // reads: soonest first, then the order they were dragged into
    project?: number;

    // limits the result to one goal's tasks
    goal?: number;

    // limits the result to the tasks this one is linked to by dependencies, however
    // many edges away and in either direction, itself included
    linkedTo?: number;

    // how many tasks make up a page. Without it the whole list is answered.
    limit?: number;

    // skips that many tasks of the list, in any order the list is read in
    offset?: number;

    // the task a page carries on from, named by its id. Without it the page starts at
    // the top of the list. Only a project's or a goal's list can be read this way.
    after?: number;

    // adds the checklist items ticked off inside the due range, on the day they were
    // completed. Without it the list holds only the items carrying a due date of their
    // own.
    includeCompletedChecklist?: boolean;
}

export class Tasks extends SubAPI {
    list(props: TaskFilter) {
        return this.fetcher.get<Task[]>("/api/tasks", props);
    }

    // listPage reads one page of a list, with the total the whole list holds
    listPage(props: TaskFilter & { limit: number }) {
        return this.fetcher.get<TaskPage>("/api/tasks", props);
    }

    get(props: { id: number }) {
        return this.fetcher.get<Task>("/api/task", props);
    }

    // ids in their new order; see the calendar day drag-drop
    reorder(props: { ids: number[] }) {
        return this.fetcher.post<null>("/api/task/reorder", props);
    }

    // ids in their new order within a project; see the project panel's task list
    reorderProject(props: { ids: number[] }) {
        return this.fetcher.post<null>("/api/task/reorder-project", props);
    }

    // ids of one task's checklist items in their new order
    reorderChecklist(props: { ids: number[] }) {
        return this.fetcher.post<null>("/api/task/reorder-checklist", props);
    }

    upsert(props: TaskInput) {
        return this.fetcher.post<Task>("/api/task", props);
    }

    // completedOn moves a task that is already complete to the day it was in fact
    // done, and moveDueDate brings a later due date along with it; see the calendar
    // day drag-drop
    //
    // undo, with completed false, also removes the next task a repeat made, while
    // nobody has changed it yet
    complete(props: { id: number; completed: boolean; completedOn?: string; moveDueDate?: boolean; undo?: boolean }) {
        return this.fetcher.post<CompletedTask>("/api/task/complete", props);
    }

    archive(props: { id: number }) {
        return this.fetcher.post<null>("/api/task/archive", props);
    }

    reschedule(props: { id: number; dueDate: string | null }) {
        return this.fetcher.post<null>("/api/task/reschedule", props);
    }

    // move puts the task and its checklist on another team. cascade takes the tasks it's
    // linked to along too.
    move(props: { id: number; team: number; cascade: boolean }) {
        return this.fetcher.post<TaskMoved>("/api/task/move", props);
    }

    // promote turns a checklist item into a standalone task
    promote(props: { id: number }) {
        return this.fetcher.post<Task>("/api/task/promote", props);
    }

    // createChain makes all the tasks and the links between them in one step, and
    // answers with the new ids in seq order
    createChain(props: { project: number; goal?: number; tasks: ChainRow[] }) {
        return this.fetcher.post<{ ids: number[] }>("/api/task/chain", props);
    }

    // bulk applies one change to every task in ids, all or none of it
    bulk(props: BulkInput) {
        return this.fetcher.post<null>("/api/task/bulk", props);
    }

    // setDependency adds or removes the edge that makes "task" wait on "dependsOn"
    setDependency(props: { task: number; dependsOn: number; archived: boolean }) {
        return this.fetcher.post<boolean>("/api/task-dependency", props);
    }

    // setFile hangs an uploaded file off a task, or takes it back off
    setFile(props: { task: number; file: number; archived: boolean }) {
        return this.fetcher.post<boolean>("/api/task-file", props);
    }

    // setComment leaves a note on a task, rewrites one, or takes it back off. Only the
    // person who left a comment can change it.
    setComment(props: { id?: number; task: number; body?: string; archived?: boolean }) {
        return this.fetcher.post<Comment>("/api/task-comment", props);
    }
}

// a "checklist" task is a sub-task of the task named by parent. Without a date of its
// own it reaches the calendar on the day it was ticked off.
export type TaskType = "task" | "checklist";

export type RepeatKind = "" | "daily" | "weekly" | "monthly" | "quarterly" | "yearly" | "periodically";
export type MonthlyMode = "" | "day-of-month" | "nth-day-of-week";
export type QuarterlyMode = MonthlyMode;
export type YearlyMode = "" | "same-date" | "nth-day-of-week";
export type PeriodicMode = "" | "nth-day-of-week" | "days";

// -1 selects the last matching day of the period
export const NthLast = -1;

export type Repeat = {
    kind: RepeatKind;

    // weekly: 0=sunday .. 6=saturday
    weekdays: number[];

    monthlyMode: MonthlyMode;
    quarterlyMode: QuarterlyMode;
    yearlyMode: YearlyMode;
    periodicMode: PeriodicMode;

    dayOfMonth: number;

    // nth-day-of-week
    nth: number;
    weekday: number;

    // yearly (nth-day-of-week): 1..12
    month: number;

    // periodically (days)
    days: number;
}

export type Assignee = {
    usr: number;
    name: string;
}

// TaskLink is a task at the other end of a dependency edge
export type TaskLink = {
    id: number;

    title: string;
    dueDate: string | null;
    dueTime: string | null;
    completedAt: string | null;

    // noAccess marks a task on a team the user isn't on. Its title, dates and
    // completion are blank.
    noAccess?: boolean;
}

export type Task = {
    id: number;
    type: TaskType;
    title: string;
    description: string;

    // "2006-01-02" or null
    dueDate: string | null;
    // "15:04" or null
    dueTime: string | null;

    completedAt: string | null;
    completedBy: number | null;
    previousTask: number | null;
    parent: number | null;
    project: number | null;

    // the piece of the project this task works towards. Setting it also sets project,
    // since a goal sits under one.
    goal: number | null;

    // who can see the task. It's set on create; after that only move changes it.
    // teamName is read-only.
    team: number;
    teamName: string;

    archived: boolean;
    createdAt: string;
    createdBy: number;
    updatedAt: string;

    // showOn is the calendar day this task belongs on, and parentTitle labels it
    // there. carriedOver marks a task showOn moved off a due date that has passed.
    // All three are read-only.
    showOn: string | null;
    carriedOver: boolean;
    parentTitle: string;

    // projectName and projectColor label the task wherever it shows outside its
    // project, goalName the same for its goal. Read-only; project and goal are the
    // fields that assign them.
    projectName: string;
    projectColor: string;
    projectCode: string;
    goalName: string;

    repeat: Repeat;
    assignedTo: Assignee[];

    // blockedBy is the tasks this one waits on, followedBy the tasks waiting on it.
    // Both are edited with setDependency, not with upsert.
    blockedBy: TaskLink[];
    followedBy: TaskLink[];

    // true while one of the blockedBy tasks is still open
    blocked: boolean;

    // the sub-tasks this one breaks down into. Edited by upserting the items
    // themselves, not through this field.
    checklist: TaskLink[];

    // the files hanging off this task. Edited with setFile, not through this field.
    attachments: Attachment[];

    // the notes left on this task, oldest first. Only a task read with get() carries
    // them; in a list they're null. Edited with setComment, not through this field.
    comments: Comment[] | null;
}

// Comment is a note somebody left on a task. body is html, from the same editor the
// task description uses.
export type Comment = {
    id: number;

    body: string;

    // createdByName labels the comment with whoever left it
    createdAt: string;
    createdBy: number;
    createdByName: string;
    updatedAt: string;
}

// Attachment is a file hanging off a task; the file itself is stored globally
// (see api/Files)
export type Attachment = {
    id: number;
    name: string;
    contentType: string;
    size: number;
    uploadedAt: string | null;
}

// assignedTo left out keeps the task's assignees; an empty list is turned away
export type TaskInput = Partial<Omit<Task, "assignedTo" | "blockedBy" | "followedBy" | "checklist" | "attachments" | "comments">> & {
    assignedTo?: { usr: number }[];
}

// BulkInput is one change applied to many tasks. A field left out leaves that part of
// each task alone. setDueDate and setProject say whether dueDate and project/goal are
// part of the change, since a null value clears them.
export type BulkInput = {
    ids: number[];
    archived?: boolean;
    completed?: boolean;

    setDueDate?: boolean;
    dueDate?: string | null;

    setProject?: boolean;
    project?: number | null;
    goal?: number | null;

    // blockedBy is tasks each task is made to wait on, followedBy tasks made to wait
    // on each task. Links that are already there are kept.
    blockedBy?: number[];
    followedBy?: number[];
}

// TaskPage is one page of a list, and the total the whole list holds
export type TaskPage = {
    data: Task[];
    totalCount: number;
}

export type CompletedTask = Task & {
    // nextRepeatKept is true when an undo left the next repeat in place, since it had
    // been changed
    nextRepeatKept: boolean;
}

export type TaskMoved = {
    moved: number;

    // the tasks whose project stayed on the old team
    unlinkedFromProject: number;

    // the assignees removed because they aren't on the new team
    unassigned: number;
}

// ChainRow is one task of a chain. blockedBySeq names other rows of the same chain,
// blockedByTask existing tasks.
export type ChainRow = {
    seq: number;
    title: string;
    dueDate: string | null;
    description: string;
    files: number[];
    assignedTo: number[];
    blockedBySeq: number[];
    blockedByTask: number[];
}

export const emptyRepeat: Repeat = {
    kind: "",
    weekdays: [],
    monthlyMode: "",
    quarterlyMode: "",
    yearlyMode: "",
    periodicMode: "",
    dayOfMonth: 0,
    nth: 0,
    weekday: 0,
    month: 0,
    days: 0,
};
